import { useCallback, useEffect, useState } from "react";
import {
  EntrepotService,
  CompanyServices,
  EntreprisesServices,
  ProductService,
} from "$services";
import { CompanyType } from "$types";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

export const usePatchCompany = (companyId) => {
  const token = localStorage.getItem("access_token");
  const [isLoading, setIsLoading] = useState(false);

  // all this are crap to remove
  const [clients, setClients] = useState([]);
  const [suppliers, setSupplier] = useState([]);
  const [carrier, setCarrier] = useState(null);
  const [logiWarehouse, setLogiWarehouse] = useState([]);
  const [clientsWithCatalogue, setClientsWithCatalogue] = useState([]);
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [contributors, setContributors] = useState<
    EntreprisesServices.ICompany[]
  >([]);

  const getCompany = useCallback(async () => {
    let company;
    company = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANY_BY_ID,
      companyId,
    ]);
    if (!company) {
      company = await queryClient.fetchQuery(
        [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
        () => CompanyServices.getCompanyById(companyId),
        { cacheTime: 60 * 1000 }
      );
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
        company
      );
    }
    setCompany(company);
  }, [setCompany, companyId]);

  useEffect(() => {
    getCompany();
  }, [getCompany]);

  const fecthCompanies = useCallback(async () => {
    const filters = {
      page: 1,
      rowsPerPage: 50000,
    };
    let companies;
    companies = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companies) {
      companies = await queryClient.fetchQuery(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        () => CompanyServices.getCompanies(filters),
        { cacheTime: 60 * 1000 }
      );
      companies = companies.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companies
      );
    }
    setCompanies(companies);
  }, [setCompanies]);

  useEffect(() => {
    fecthCompanies();
  }, [fecthCompanies]);

  const fetchClientCatalogue = useCallback(async () => {
    const data = await Promise.all(
      clients?.map((c) => {
        if (c.isActive) {
          return (
            queryClient.fetchQuery(["productByCompany", c._id], () =>
              ProductService.getProductByReference({ company: c._id })
            ),
            {
              cacheTime: 1000 * 60,
            }
          );
        }
        return false;
      })
    );
    setClientsWithCatalogue(data.filter(Boolean).flat());
  }, [clients]);

  useEffect(() => {
    fetchClientCatalogue();
  }, [fetchClientCatalogue]);

  const fetchContributor = useCallback(() => {
    setIsLoading(true);
    EntreprisesServices.FetchCompaniesContributorsFromCompany(
      companyId,
      token
    ).then((response) => {
      setContributors(response || []);
      setIsLoading(false);
    });
  }, [companyId, token]);

  const fetchClients = useCallback(async () => {
    const cFetch = companies.filter((c) =>
      company?.contributorOf?.some((c2) => c2._id === c._id)
    );
    setClients(cFetch);
  }, [companies, company?.contributorOf]);

  const fetchSupplier = useCallback(() => {
    setSupplier(
      companies.filter((c) => company?.supplierOf?.some((c2) => c2 === c._id))
    );
  }, [companies, company?.supplierOf]);

  const fetchCarrier = useCallback(() => {
    setCarrier(companies.find((c) => c._id === company?.agencyOf));
  }, [companies, company?.agencyOf]);

  const fetchWarehouse = useCallback(async () => {
    const filters = {
      ...{ company: company?._id },
    };
    const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
    let getWarehouses: any = await queryClient.getQueryData(queryKey);
    if (!getWarehouses) {
      getWarehouses = await queryClient.fetchQuery(
        queryKey,
        () => EntrepotService.getEntrepots(filters),
        { cacheTime: 5 * 60 * 1000 }
      );
      queryClient.setQueryData(queryKey, getWarehouses);
    }

    setLogiWarehouse(
      (getWarehouses?.data || []).reduce((res, e) => {
        if (e.parentCompany === company?._id) {
          const toAddCompanyIds = e.companies
            .map((c) => c._id)
            .filter(
              (wCompanyId) =>
                !res.some((addedCompany) => addedCompany._id !== wCompanyId) &&
                wCompanyId !== company._id
            );
          return [
            ...res,
            ...companies.filter((c) => toAddCompanyIds.includes(c._id)),
          ];
        }
        return res;
      }, [])
    );
  }, [companies, company?._id]);

  useEffect(() => {
    if (companyId) {
      if (company?.companyTypes?.includes(CompanyType.CLIENT)) {
        fetchContributor();
      }
      if (company?.companyTypes?.includes(CompanyType.CONTRIBUTOR)) {
        fetchClients();
      }
      fetchSupplier();
      if (company?.companyTypes?.includes(CompanyType.AGENCY)) {
        fetchCarrier();
      }
      if (company?.companyTypes?.includes(CompanyType.LOGISTICIAN)) {
        fetchWarehouse();
      }
    }
  }, [
    company?.companyTypes,
    companyId,
    fetchCarrier,
    fetchClients,
    fetchContributor,
    fetchSupplier,
    fetchWarehouse,
  ]);

  return {
    isLoading,
    company,
    getCompany,
    contributors,
    clients,
    suppliers,
    carrier,
    logiWarehouse,
    clientsWithCatalogue,
  };
};
